import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Check, X, ZoomIn, ZoomOut, Undo2, Redo2,
  Maximize2, Minimize2, Hand, MapPin, Square, Edit3,
  AlignLeft, AlignRight, AlignCenterHorizontal, AlignVerticalJustifyCenter,
  AlignStartHorizontal, AlignEndHorizontal, AlignHorizontalSpaceAround,
  AlignVerticalSpaceAround, AlertTriangle, ChevronDown,
} from "lucide-react";
import {
  polygonSelfIntersects, snapToGrid, alignToVertices,
  alignVertices, distributeVertices,
} from "@/lib/polygonGeom";
import { snapToRuler, snapAngle, rulerEndpoints, lineLineIntersection, segSegIntersection } from "@/lib/rulerGeom";
import Rulers from "./zone-editor/Rulers";
import TranslationsEditor from "./TranslationsEditor";
import {
  MIN_ZOOM, MAX_ZOOM, VERTEX_SNAP_PX, ACTION_LABEL, isVideoScene,
  SNAP_KIND_DEFAULTS, SNAP_KIND_LABEL,
} from "./zone-editor/constants";

/**
 * Polygon + point ("twinkle") zone editor.
 *
 * Drawing modes:
 *   - "polygon" — click to drop vertices; finish with ≥3 points.
 *   - "point"   — single click drops a twinkle marker that renders as a
 *                 pulsing dot in the public viewer.
 *
 * Snap (vertex snap, 1.b):
 *   - While drawing a polygon, the next point auto-snaps to any existing
 *     zone vertex within VERTEX_SNAP_PX of the cursor. A green ring marker
 *     telegraphs the snap to the user.
 *
 * Per-zone click action (3.a + 3.b + 3.c):
 *   - open_details  → open the linked unit / group panel (legacy)
 *   - show_info     → open an info card (title + description + image)
 *   - goto_scene    → jump to another scene (prefers scrubbable transition)
 */

export default function ZoneEditor({ scene, zones, entities, groups, scenes, pid, project, onChange }) {
  const wrapRef = useRef(null);
  const fullscreenRef = useRef(null);
  const mediaRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 450 });
  const [mediaSize, setMediaSize] = useState({ w: 1920, h: 1080 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [expanded, setExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const dragRef = useRef({ active: false, kind: null, startX: 0, startY: 0, panX: 0, panY: 0 });

  const [drawMode, setDrawMode] = useState("polygon"); // 'polygon' | 'point'
  const [drawing, setDrawing] = useState(false);
  const [points, setPoints] = useState([]);
  const [pointCoord, setPointCoord] = useState(null);  // only when drawMode='point'
  const [snapPreview, setSnapPreview] = useState(null); // {x, y, kind} in normalized coords
  const [snapEnabled, setSnapEnabled] = useState(true);
  // Phase B — per-kind CAD snap toggles (endpoint/midpoint/intersection/…),
  // persisted so an admin's setup survives reloads.
  const [snapKinds, setSnapKinds] = useState(() => {
    try { return { ...SNAP_KIND_DEFAULTS, ...JSON.parse(localStorage.getItem("ze_snap_kinds") || "{}") }; }
    catch { return { ...SNAP_KIND_DEFAULTS }; }
  });
  const [snapMenuOpen, setSnapMenuOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem("ze_snap_kinds", JSON.stringify(snapKinds)); } catch { /* ignore */ }
  }, [snapKinds]);
  // Phase 5C — alignment guides shown while dragging/drawing. Each guide is
  // {type:'v'|'h', value} in normalized media space.
  const [snapGuides, setSnapGuides] = useState([]);
  // Phase 3.5 — Figma-style editing aids.
  const [selectedVertex, setSelectedVertex] = useState(null); // legacy single
  // Phase 5C — multi-vertex selection (Shift+click). Always kept in sync
  // with `selectedVertex` so single-select keyboard / drag still works.
  const [selectedVertices, setSelectedVertices] = useState([]); // array of indices
  const [gridEnabled, setGridEnabled] = useState(false);
  const [gridSize, setGridSize] = useState(32); // pixels in media-coord space
  const [gridSnapEnabled, setGridSnapEnabled] = useState(false); // Phase 5C
  const [rulersEnabled, setRulersEnabled] = useState(false);
  // Iter36 — Morpholio-Trace-style ruler. When active, drawn/dragged vertices
  // snap onto the ruler beam. Position (cx,cy) is the mid-point of the visible
  // segment, angleDeg rotates around cx,cy. In "perspective" mode the beam
  // pivots around (vanishX, vanishY) — like a T-square anchored to a
  // vanishing point.
  const [ruler, setRuler] = useState({
    enabled: false,
    mode: "straight",           // "straight" | "perspective"
    cx: 0.5, cy: 0.5,           // ruler center in normalized coords
    angleDeg: 0,                // 0 = horizontal, 90 = vertical
    vanishX: 0.5, vanishY: 0.15, // used in "perspective" mode
  });
  const rulerDragRef = useRef({ active: false, kind: null, startX: 0, startY: 0,
                                startAngleDeg: 0, startCx: 0, startCy: 0,
                                startVanishX: 0, startVanishY: 0 });
  // iter56 — Multiple infinite guide lines, saved per scene. Each guide is
  // { id, x, y, angleDeg }: an infinite line through the normalized point
  // (x,y) at angleDeg. Used as a perspective construction grid; vertices snap
  // onto guide lines and their intersections while drawing/editing.
  const [guides, setGuides] = useState([]);
  const [selectedGuideId, setSelectedGuideId] = useState(null);
  const guideDragRef = useRef({ active: false, kind: null, id: null });
  const guideSaveRef = useRef(null);
  useEffect(() => {
    setGuides(Array.isArray(scene?.editor_guides) ? scene.editor_guides : []);
    setSelectedGuideId(null);
  }, [scene?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const persistGuides = (g) => {
    if (guideSaveRef.current) clearTimeout(guideSaveRef.current);
    guideSaveRef.current = setTimeout(() => {
      api.put(`/admin/scenes/${scene.id}`, { editor_guides: g }).catch(() => {});
    }, 600);
  };
  const addGuide = () => {
    const g = { id: `gd-${Date.now()}`, x: 0.5, y: 0.5, angleDeg: 90 };
    setGuides((prev) => { const next = [...prev, g]; persistGuides(next); return next; });
    setSelectedGuideId(g.id);
  };
  const deleteGuide = (id) => {
    setGuides((prev) => { const next = prev.filter((x) => x.id !== id); persistGuides(next); return next; });
    setSelectedGuideId((s) => (s === id ? null : s));
  };
  const updateGuide = (id, patch) => {
    setGuides((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };
  const onGuideDown = (e, id, kind) => {
    e.stopPropagation(); e.preventDefault();
    setSelectedGuideId(id);
    const raw = fromScreen(e.clientX, e.clientY);
    const g = guides.find((x) => x.id === id);
    guideDragRef.current = { active: true, kind, id, startRawX: raw.x, startRawY: raw.y,
                             startGx: g?.x ?? 0.5, startGy: g?.y ?? 0.5 };
    try { wrapRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  // Phase 5C — undo/redo stack for the in-progress polygon. We snapshot
  // `points` on every committed mutation (add / drag-end / delete /
  // midpoint-insert / align / distribute / paste / clear). Drag MOVE
  // events update `points` directly and only push a snapshot on POINTERUP.
  const historyRef = useRef({ stack: [[]], index: 0 });
  // Phase 5C — live self-intersection warning state. Derived via useMemo
  // below from `points`; this just exposes the dismiss state.
  const [intersectDismissed, setIntersectDismissed] = useState(false);
  // Drag state for an existing vertex. We use the same `dragRef` for pan and
  // first-click placement; vertex drag lives separately so the two don't fight.
  const vertexDragRef = useRef({ active: false, index: -1 });
  const [target, setTarget] = useState({
    zone_type: "entity", entity_id: "", group_id: "",
    click_action: "open_details", target_scene_id: "",
    info_title: "", info_description: "", info_image_url: "",
  });
  const [editingId, setEditingId] = useState(null); // existing zone being edited
  const [name, setName] = useState("");
  const [translations, setTranslations] = useState({});

  // Container resize
  useEffect(() => {
    const upd = () => {
      const el = wrapRef.current;
      if (!el) return;
      setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    };
    upd();
    const ro = new ResizeObserver(upd);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", upd);
    return () => { ro.disconnect(); window.removeEventListener("resize", upd); };
  }, [expanded, isFullscreen]);

  // Reset transform + drawing state when scene changes
  useEffect(() => {
    setZoom(1); setPan({ x: 0, y: 0 });
    setPoints([]); setPointCoord(null); setDrawing(false); setEditingId(null);
    setTranslations({});
    setSnapPreview(null); setSnapGuides([]);
    setSelectedVertex(null); setSelectedVertices([]);
    historyRef.current = { stack: [[]], index: 0 };
    setIntersectDismissed(false);
  }, [scene?.id]);

  // Phase 5C — history helpers. `commitHistory(next)` becomes the new
  // current state and pushes a snapshot, dropping any redo branch.
  // `setPoints` is split into a "silent" path (during drag move) that
  // bypasses history, and a "commit" path used by add/delete/align/etc.
  const commitHistory = useCallback((next) => {
    setPoints(next);
    const h = historyRef.current;
    const trimmed = h.stack.slice(0, h.index + 1);
    trimmed.push(next);
    historyRef.current = { stack: trimmed, index: trimmed.length - 1 };
    setIntersectDismissed(false);
  }, []);
  const canUndo = () => historyRef.current.index > 0;
  const canRedo = () => historyRef.current.index < historyRef.current.stack.length - 1;
  const doUndo = useCallback(() => {
    const h = historyRef.current;
    if (h.index <= 0) return;
    h.index -= 1;
    setPoints(h.stack[h.index]);
    setSelectedVertex(null); setSelectedVertices([]);
    setSnapGuides([]); setSnapPreview(null);
  }, []);
  const doRedo = useCallback(() => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    h.index += 1;
    setPoints(h.stack[h.index]);
    setSelectedVertex(null); setSelectedVertices([]);
    setSnapGuides([]); setSnapPreview(null);
  }, []);

  // Phase 5C — derived self-intersection state. Rendered only when
  // drawing a polygon with ≥4 points.
  const isSelfIntersecting = useMemo(
    () => drawing && drawMode === "polygon" && polygonSelfIntersects(points),
    [drawing, drawMode, points],
  );

  // Fullscreen sync
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const down = (e) => {
      if (e.code === "Space" && !e.repeat) { setSpaceHeld(true); e.preventDefault(); return; }
      const meta = e.ctrlKey || e.metaKey;
      // Cmd/Ctrl+Shift+Z OR Cmd/Ctrl+Y → redo. Otherwise Cmd/Ctrl+Z → undo.
      if (meta && (e.key === "z" || e.key === "Z") && e.shiftKey) {
        if (drawing && drawMode === "polygon") { doRedo(); e.preventDefault(); }
      } else if (meta && (e.key === "y" || e.key === "Y")) {
        if (drawing && drawMode === "polygon") { doRedo(); e.preventDefault(); }
      } else if (meta && (e.key === "z" || e.key === "Z")) {
        if (drawing && drawMode === "polygon") { doUndo(); e.preventDefault(); }
      } else if (!meta && (e.key === "Delete" || e.key === "Backspace") && drawing && drawMode === "polygon") {
        // Phase 5C — keyboard-delete one or more selected vertices.
        const idx = selectedVertices.length > 0 ? selectedVertices : (selectedVertex != null ? [selectedVertex] : []);
        if (idx.length === 0) return;
        if (points.length - idx.length < 3) {
          toast.error("Polygon needs at least 3 vertices");
          return;
        }
        const drop = new Set(idx);
        commitHistory(points.filter((_, i) => !drop.has(i)));
        setSelectedVertex(null); setSelectedVertices([]);
        e.preventDefault();
      } else if (!meta && (e.key === "+" || e.key === "=")) {
        setZoom(z => Math.min(MAX_ZOOM, z * 1.25));
      } else if (!meta && (e.key === "-" || e.key === "_")) {
        setZoom(z => Math.max(MIN_ZOOM, z / 1.25));
      } else if (!meta && e.key === "0") {
        setZoom(1); setPan({ x: 0, y: 0 });
      } else if (e.key === "Escape" && drawing) {
        setDrawing(false); setPoints([]); setPointCoord(null);
        setSelectedVertex(null); setSelectedVertices([]);
        historyRef.current = { stack: [[]], index: 0 };
      }
    };
    const up = (e) => { if (e.code === "Space") setSpaceHeld(false); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [drawing, drawMode, points, selectedVertex, selectedVertices, commitHistory, doUndo, doRedo]);

  // Fit math
  const fitScale = (() => {
    if (!containerSize.w || !containerSize.h || !mediaSize.w || !mediaSize.h) return 1;
    return Math.min(containerSize.w / mediaSize.w, containerSize.h / mediaSize.h);
  })();
  const totalScale = fitScale * zoom;

  // Wheel zoom anchored at cursor
  const onWheel = (e) => {
    e.preventDefault();
    const rect = wrapRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom(z => {
      const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z * factor));
      const ratio = next / z;
      setPan(p => ({ x: cx - (cx - p.x) * ratio, y: cy - (cy - p.y) * ratio }));
      return next;
    });
  };

  // Screen → normalized coord
  const fromScreen = useCallback((clientX, clientY) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    const mediaCx = containerSize.w / 2 + pan.x;
    const mediaCy = containerSize.h / 2 + pan.y;
    const mediaPxX = (sx - mediaCx) / totalScale + mediaSize.w / 2;
    const mediaPxY = (sy - mediaCy) / totalScale + mediaSize.h / 2;
    return {
      x: Math.max(0, Math.min(1, mediaPxX / mediaSize.w)),
      y: Math.max(0, Math.min(1, mediaPxY / mediaSize.h)),
    };
  }, [containerSize, pan.x, pan.y, totalScale, mediaSize]);

  // Vertex snap (Phase B rewrite): collects ALL enabled point-snap candidates
  // (vertex, intersection, midpoint, perpendicular, edge, extension) within
  // tolerance and picks the highest-priority/closest one, AutoCAD-OSNAP
  // style. Falls back to alignment guides → grid → guide-line projection →
  // ruler when no point candidate grabs. `snapPreview` carries {x, y, kind}
  // so the marker + badge telegraph WHICH snap won.
  const applyVertexSnap = useCallback((rawNorm) => {
    if (drawMode !== "polygon") { setSnapPreview(null); setSnapGuides([]); return rawNorm; }
    const tolPx = VERTEX_SNAP_PX / totalScale;
    const W = mediaSize.w, H = mediaSize.h;
    const px = rawNorm.x * W, py = rawNorm.y * H;

    // Zone + in-progress edges in media-px space (shared by several kinds).
    const edges = [];
    if (snapEnabled) {
      for (const z of zones) {
        if ((z.shape || "polygon") !== "polygon") continue;
        const poly = z.polygon_points || [];
        for (let i = 0; i < poly.length; i++) {
          const a = poly[i], b = poly[(i + 1) % poly.length];
          if (!a || !b) continue;
          edges.push({ ax: a.x * W, ay: a.y * H, bx: b.x * W, by: b.y * H });
        }
      }
      for (let i = 0; i + 1 < points.length; i++) {
        const a = points[i], b = points[i + 1];
        edges.push({ ax: a.x * W, ay: a.y * H, bx: b.x * W, by: b.y * H });
      }
    }
    const guideLines = guides.map((g) => {
      const rad = (g.angleDeg || 0) * Math.PI / 180;
      return { cx: g.x * W, cy: g.y * H, dx: Math.cos(rad), dy: Math.sin(rad) };
    });

    // Priority: vertex(0) > intersection(1) > midpoint(2) > perpendicular(3)
    // > edge(4) > extension(5). Ties broken by distance.
    let best = null;
    const consider = (x, y, kind, priority) => {
      const d = Math.hypot(px - x, py - y);
      if (d > tolPx) return;
      if (!best || priority < best.priority || (priority === best.priority && d < best.d)) {
        best = { x, y, kind, d, priority };
      }
    };

    if (snapEnabled && snapKinds.vertex) {
      for (const z of zones) {
        if ((z.shape || "polygon") !== "polygon") continue;
        for (const p of (z.polygon_points || [])) consider(p.x * W, p.y * H, "vertex", 0);
      }
      for (const p of points) consider(p.x * W, p.y * H, "vertex", 0);
    }
    if (snapKinds.intersection) {
      for (let i = 0; i < guideLines.length; i++) {
        for (let j = i + 1; j < guideLines.length; j++) {
          const ix = lineLineIntersection(guideLines[i], guideLines[j]);
          if (ix) consider(ix.x, ix.y, "intersection", 1);
        }
      }
      if (snapEnabled) {
        for (const gl of guideLines) {
          for (const e of edges) {
            const evx = e.bx - e.ax, evy = e.by - e.ay;
            const len = Math.hypot(evx, evy) || 1;
            const ix = lineLineIntersection(gl, { cx: e.ax, cy: e.ay, dx: evx / len, dy: evy / len });
            if (!ix) continue;
            const t = ((ix.x - e.ax) * evx + (ix.y - e.ay) * evy) / (len * len);
            if (t >= -1e-6 && t <= 1 + 1e-6) consider(ix.x, ix.y, "intersection", 1);
          }
        }
        for (let i = 0; i < edges.length; i++) {
          for (let j = i + 1; j < edges.length; j++) {
            const ix = segSegIntersection(
              { x: edges[i].ax, y: edges[i].ay }, { x: edges[i].bx, y: edges[i].by },
              { x: edges[j].ax, y: edges[j].ay }, { x: edges[j].bx, y: edges[j].by });
            if (ix) consider(ix.x, ix.y, "intersection", 1);
          }
        }
      }
    }
    if (snapEnabled && snapKinds.midpoint) {
      for (const e of edges) consider((e.ax + e.bx) / 2, (e.ay + e.by) / 2, "midpoint", 2);
    }
    if (snapKinds.perpendicular && points.length > 0) {
      // Foot of the perpendicular dropped from the LAST placed point.
      const last = points[points.length - 1];
      const lx = last.x * W, ly = last.y * H;
      for (const e of edges) {
        const vx = e.bx - e.ax, vy = e.by - e.ay;
        const len2 = vx * vx + vy * vy || 1;
        const t = ((lx - e.ax) * vx + (ly - e.ay) * vy) / len2;
        if (t < 0 || t > 1) continue;
        consider(e.ax + t * vx, e.ay + t * vy, "perpendicular", 3);
      }
      for (const gl of guideLines) {
        const t = (lx - gl.cx) * gl.dx + (ly - gl.cy) * gl.dy;
        consider(gl.cx + gl.dx * t, gl.cy + gl.dy * t, "perpendicular", 3);
      }
    }
    if (snapEnabled && snapKinds.edge) {
      for (const e of edges) {
        const vx = e.bx - e.ax, vy = e.by - e.ay;
        const len2 = vx * vx + vy * vy || 1;
        let t = ((px - e.ax) * vx + (py - e.ay) * vy) / len2;
        t = Math.max(0, Math.min(1, t));
        consider(e.ax + t * vx, e.ay + t * vy, "edge", 4);
      }
    }
    if (snapEnabled && snapKinds.extension) {
      for (const e of edges) {
        const vx = e.bx - e.ax, vy = e.by - e.ay;
        const len2 = vx * vx + vy * vy || 1;
        const t = ((px - e.ax) * vx + (py - e.ay) * vy) / len2;
        if (t >= 0 && t <= 1) continue;      // on-segment → that's edge snap
        if (t < -4 || t > 5) continue;       // don't extend forever
        consider(e.ax + t * vx, e.ay + t * vy, "extension", 5);
      }
    }
    if (best) {
      const out = { x: best.x / W, y: best.y / H };
      setSnapPreview({ ...out, kind: best.kind });
      setSnapGuides([]);
      return out;
    }

    // ── No point snap → weaker line/axis snaps ──────────────────────────
    let out = rawNorm;
    const alignGuides = [];
    if (snapEnabled) {
      const tolUnit = tolPx / W;
      const others = [];
      for (const z of zones) {
        if ((z.shape || "polygon") !== "polygon") continue;
        for (const p of (z.polygon_points || [])) others.push(p);
      }
      for (const p of points) others.push(p);
      const aligned = alignToVertices(rawNorm, others, tolUnit);
      out = { x: aligned.x, y: aligned.y };
      alignGuides.push(...aligned.guides);
    }
    if (gridSnapEnabled && gridEnabled) {
      out = snapToGrid(out, gridSize, mediaSize);
    }
    // Guide-line projection — guides exist to be drawn along, so this stays
    // active independent of the point-kind toggles.
    if (guideLines.length) {
      let bg = null, bgD = Infinity;
      const ox = out.x * W, oy = out.y * H;
      for (const gl of guideLines) {
        const t = (ox - gl.cx) * gl.dx + (oy - gl.cy) * gl.dy;
        const jx = gl.cx + gl.dx * t, jy = gl.cy + gl.dy * t;
        const d = Math.hypot(ox - jx, oy - jy);
        if (d < tolPx && d < bgD) { bgD = d; bg = { x: jx / W, y: jy / H }; }
      }
      if (bg) {
        setSnapPreview({ ...bg, kind: "guide" });
        setSnapGuides(alignGuides);
        return bg;
      }
    }
    // Iter36 — Ruler snap (T-square behaviour).
    if (ruler.enabled) {
      const tolUnit = tolPx / H;
      const snapped = snapToRuler(out, ruler, tolUnit);
      if (snapped) {
        out = { x: snapped.x, y: snapped.y };
      }
    }
    setSnapPreview(null);
    setSnapGuides(alignGuides);
    return out;
  }, [snapEnabled, snapKinds, drawMode, zones, points, mediaSize, totalScale, gridSnapEnabled, gridEnabled, gridSize, ruler, guides]);

  const onPointerDown = (e) => {
    if (e.button === 1 || spaceHeld) {
      dragRef.current = { active: true, kind: "pan", startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
      e.preventDefault();
      try { wrapRef.current.setPointerCapture(e.pointerId); } catch { /* ignore pointer-capture errors */ }
      return;
    }
    if (e.button !== 0) return;
    if (drawing) {
      dragRef.current = { active: true, kind: "click", startX: e.clientX, startY: e.clientY };
    }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (d.active && d.kind === "pan") {
      setPan({ x: d.panX + (e.clientX - d.startX), y: d.panY + (e.clientY - d.startY) });
      return;
    }
    // Iter36 — Ruler drag / rotate / vanish-point move.
    const rd = rulerDragRef.current;
    if (rd.active) {
      const raw = fromScreen(e.clientX, e.clientY);
      if (rd.kind === "translate") {
        // Preserve angle / vanishing point; just translate the center.
        const dxUnit = raw.x - rd.startCanvasNormX;
        const dyUnit = raw.y - rd.startCanvasNormY;
        setRuler((r) => ({
          ...r,
          cx: rd.startCx + dxUnit,
          cy: rd.startCy + dyUnit,
          vanishX: r.mode === "perspective" ? rd.startVanishX + dxUnit : r.vanishX,
          vanishY: r.mode === "perspective" ? rd.startVanishY + dyUnit : r.vanishY,
        }));
      } else if (rd.kind === "rotate") {
        // Angle from ruler center to current pointer, snap to 15° when Shift held.
        setRuler((r) => {
          const cxPx = r.cx * mediaSize.w;
          const cyPx = r.cy * mediaSize.h;
          const raw2 = fromScreen(e.clientX, e.clientY);
          const dx = raw2.x * mediaSize.w - cxPx;
          const dy = raw2.y * mediaSize.h - cyPx;
          let deg = Math.atan2(dy, dx) * 180 / Math.PI;
          if (e.shiftKey) deg = snapAngle(deg, 15);
          return { ...r, angleDeg: deg };
        });
      } else if (rd.kind === "vanish") {
        setRuler((r) => ({ ...r, vanishX: raw.x, vanishY: raw.y }));
      }
      return;
    }
    // iter56 — Guide line drag (move / rotate).
    const gdrag = guideDragRef.current;
    if (gdrag.active) {
      const raw = fromScreen(e.clientX, e.clientY);
      if (gdrag.kind === "move") {
        updateGuide(gdrag.id, { x: gdrag.startGx + (raw.x - gdrag.startRawX), y: gdrag.startGy + (raw.y - gdrag.startRawY) });
      } else if (gdrag.kind === "rotate") {
        const g = guides.find((x) => x.id === gdrag.id);
        if (g) {
          const dx = raw.x * mediaSize.w - g.x * mediaSize.w;
          const dy = raw.y * mediaSize.h - g.y * mediaSize.h;
          let deg = Math.atan2(dy, dx) * 180 / Math.PI;
          if (e.shiftKey) deg = snapAngle(deg, 15);
          updateGuide(gdrag.id, { angleDeg: deg });
        }
      }
      return;
    }
    // Phase 3.5 — drag a previously-placed vertex (silent, no history push)
    const vd = vertexDragRef.current;
    if (vd.active && drawing && drawMode === "polygon") {
      const raw = fromScreen(e.clientX, e.clientY);
      const snapped = snapEnabled ? applyVertexSnap(raw) : raw;
      setPoints((pts) => {
        if (vd.index < 0 || vd.index >= pts.length) return pts;
        const next = pts.slice();
        next[vd.index] = snapped;
        return next;
      });
      vd.moved = true;
      return;
    }
    // Live snap preview while drawing polygons
    if (drawing && drawMode === "polygon") {
      applyVertexSnap(fromScreen(e.clientX, e.clientY));
    }
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    const vd = vertexDragRef.current;
    const rd = rulerDragRef.current;
    try { wrapRef.current.releasePointerCapture?.(e.pointerId); } catch { /* ignore pointer-capture errors */ }
    if (rd.active) {
      rulerDragRef.current = { active: false, kind: null };
      return;
    }
    const gdrag = guideDragRef.current;
    if (gdrag.active) {
      guideDragRef.current = { active: false, kind: null, id: null };
      setGuides((g) => { persistGuides(g); return g; });
      return;
    }
    if (vd.active) {
      // Commit the drag (snapshot final position) only if it actually moved.
      if (vd.moved) commitHistory(points);
      vertexDragRef.current = { active: false, index: -1, moved: false };
      setSnapPreview(null); setSnapGuides([]);
      return;
    }
    if (!d.active) return;
    const dx = Math.abs(e.clientX - d.startX);
    const dy = Math.abs(e.clientY - d.startY);
    if (d.kind === "click" && drawing && dx < 4 && dy < 4) {
      const raw = fromScreen(e.clientX, e.clientY);
      if (drawMode === "polygon") {
        const snapped = applyVertexSnap(raw);
        commitHistory([...points, snapped]);
      } else if (drawMode === "point") {
        setPointCoord(raw);
      }
    }
    dragRef.current = { active: false, kind: null };
    setSnapGuides([]);
  };

  // Iter36 — Start dragging one of the ruler handles.
  const onRulerHandleDown = (e, kind) => {
    e.stopPropagation();
    e.preventDefault();
    const raw = fromScreen(e.clientX, e.clientY);
    rulerDragRef.current = {
      active: true, kind,
      startCanvasNormX: raw.x, startCanvasNormY: raw.y,
      startCx: ruler.cx, startCy: ruler.cy,
      startAngleDeg: ruler.angleDeg,
      startVanishX: ruler.vanishX, startVanishY: ruler.vanishY,
    };
    try { wrapRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  // Phase 3.5 — start dragging an existing vertex.
  // Phase 5C — Shift+click toggles the vertex into a multi-selection
  // (used by the Align / Distribute toolbar).
  const onVertexPointerDown = (e, index) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.shiftKey) {
      setSelectedVertices((cur) => {
        const has = cur.includes(index);
        return has ? cur.filter((i) => i !== index) : [...cur, index];
      });
      setSelectedVertex(index);
      return; // don't start drag when multi-toggling
    }
    setSelectedVertex(index);
    setSelectedVertices([index]);
    vertexDragRef.current = { active: true, index, moved: false };
    try { wrapRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  // Phase 3.5 — right-click a vertex to delete it.
  const onVertexContextMenu = (e, index) => {
    e.preventDefault(); e.stopPropagation();
    if (points.length <= 3) {
      toast.error("Polygon needs at least 3 vertices");
      return;
    }
    commitHistory(points.filter((_, i) => i !== index));
    setSelectedVertex(null); setSelectedVertices([]);
  };
  // Phase 3.5 — click a midpoint to insert a new vertex on that edge.
  const onMidpointClick = (e, edgeIndex) => {
    e.stopPropagation();
    const a = points[edgeIndex];
    const b = points[(edgeIndex + 1) % points.length];
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const next = points.slice();
    next.splice(edgeIndex + 1, 0, mid);
    commitHistory(next);
  };

  // Phase 5C — align / distribute the multi-selection.
  const applyAlign = (mode) => {
    const idx = selectedVertices.length >= 2 ? selectedVertices : (selectedVertex != null ? [selectedVertex] : []);
    if (idx.length < 2) { toast.error("Select 2+ vertices (Shift-click) to align"); return; }
    commitHistory(alignVertices(points, idx, mode));
  };
  const applyDistribute = (axis) => {
    const idx = selectedVertices.length >= 3 ? selectedVertices : [];
    if (idx.length < 3) { toast.error("Select 3+ vertices (Shift-click) to distribute"); return; }
    commitHistory(distributeVertices(points, idx, axis));
  };

  // Compose the payload for create/update
  const buildPayload = () => {
    const base = {
      project_id: pid, scene_id: scene.id,
      translations,
      zone_type: target.zone_type,
      shape: drawMode,
      polygon_points: drawMode === "polygon" ? points : [],
      point_coord: drawMode === "point" ? pointCoord : null,
      click_action: target.click_action,
      target_scene_id: target.click_action === "goto_scene" ? (target.target_scene_id || null) : null,
      info_title: target.click_action === "show_info" ? (target.info_title || null) : null,
      info_description: target.click_action === "show_info" ? (target.info_description || null) : null,
      info_image_url: target.click_action === "show_info" ? (target.info_image_url || null) : null,
      entity_id: target.zone_type === "entity" ? (target.entity_id || null) : null,
      group_id: target.zone_type === "group" ? (target.group_id || null) : null,
      name: name || (
        target.zone_type === "entity" ? entities.find(e => e.id === target.entity_id)?.code :
        target.zone_type === "group"  ? groups.find(g => g.id === target.group_id)?.name :
        target.info_title || "Zone"
      ) || "Zone",
      visibility: true,
      sort_order: zones.length,
    };
    return base;
  };

  const validate = () => {
    if (drawMode === "polygon" && points.length < 3) { toast.error("Polygon needs at least 3 points"); return false; }
    if (drawMode === "point" && !pointCoord) { toast.error("Tap the canvas to place the point"); return false; }
    if (target.click_action === "open_details") {
      if (target.zone_type === "entity" && !target.entity_id) { toast.error("Pick a unit"); return false; }
      if (target.zone_type === "group"  && !target.group_id ) { toast.error("Pick a group"); return false; }
    }
    if (target.click_action === "goto_scene" && !target.target_scene_id) { toast.error("Pick a target scene"); return false; }
    if (target.click_action === "show_info" && !(target.info_title || name)) { toast.error("Add an info title"); return false; }
    return true;
  };

  const finishZone = async () => {
    if (!validate()) return;
    // Phase 5C — warn (don't block) when the polygon's edges cross.
    if (drawMode === "polygon" && polygonSelfIntersects(points) && !intersectDismissed) {
      setIntersectDismissed(true);
      toast.warning("Polygon self-intersects — clicking Finish once more will save it as-is.");
      return;
    }
    try {
      const payload = buildPayload();
      if (editingId) await api.put(`/admin/zones/${editingId}`, payload);
      else await api.post("/admin/zones", payload);
      toast.success(editingId ? "Zone updated" : "Zone saved");
      resetForm();
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const resetForm = () => {
    setDrawing(false); setEditingId(null);
    setPoints([]); setPointCoord(null); setName(""); setSnapPreview(null); setSnapGuides([]);
    setTranslations({});
    setSelectedVertex(null); setSelectedVertices([]);
    historyRef.current = { stack: [[]], index: 0 };
    setIntersectDismissed(false);
    setTarget({
      zone_type: "entity", entity_id: "", group_id: "",
      click_action: "open_details", target_scene_id: "",
      info_title: "", info_description: "", info_image_url: "",
    });
  };

  const editZone = (z) => {
    setEditingId(z.id);
    setDrawing(true);
    setDrawMode(z.shape || "polygon");
    const initialPoints = z.polygon_points || [];
    setPoints(initialPoints);
    setPointCoord(z.point_coord || null);
    setName(z.name || "");
    setTranslations(z.translations || {});
    setSelectedVertex(null); setSelectedVertices([]);
    historyRef.current = { stack: [initialPoints], index: 0 };
    setIntersectDismissed(false);
    setTarget({
      zone_type: z.zone_type || "entity",
      entity_id: z.entity_id || "",
      group_id: z.group_id || "",
      click_action: z.click_action || "open_details",
      target_scene_id: z.target_scene_id || "",
      info_title: z.info_title || "",
      info_description: z.info_description || "",
      info_image_url: z.info_image_url || "",
    });
  };

  const delZone = async (id) => { if (!confirm("Delete zone?")) return; await api.delete(`/admin/zones/${id}`); onChange(); };
  const fitToScreen = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (fullscreenRef.current) await fullscreenRef.current.requestFullscreen();
    } catch (e) {
      toast.error("Fullscreen not supported in this browser");
    }
  };

  const polyMediaPx = (pts) => pts.map(p => `${p.x * mediaSize.w},${p.y * mediaSize.h}`).join(" ");
  const inProgressPx = points.map(p => `${p.x * mediaSize.w},${p.y * mediaSize.h}`).join(" ");

  const canvasHeight = isFullscreen ? "100vh" : (expanded ? "78vh" : "min(64vh, 600px)");
  const cursor = dragRef.current.active && dragRef.current.kind === "pan"
    ? "grabbing"
    : spaceHeld
      ? "grab"
      : (drawing ? "crosshair" : "default");

  const wrapperStyle = {
    position: "absolute",
    left: containerSize.w / 2 + pan.x,
    top: containerSize.h / 2 + pan.y,
    width: mediaSize.w,
    height: mediaSize.h,
    transform: `translate(-50%, -50%) scale(${totalScale})`,
    transformOrigin: "center center",
    pointerEvents: "none",
  };

  const otherScenes = (scenes || []).filter(s => s.id !== scene?.id);

  return (
    <div ref={fullscreenRef} className={`grid ${isFullscreen ? "grid-cols-1" : "lg:grid-cols-[1fr_360px]"} gap-6 ${isFullscreen ? "fixed inset-0 z-50 bg-zinc-950 p-4" : ""}`} data-testid="zone-editor-root">
      <div className="flex flex-col gap-3">
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1">
            <button onClick={() => setZoom(z => Math.max(MIN_ZOOM, z / 1.25))} className="p-1.5 hover:bg-zinc-100 rounded" title="Zoom out (−)" data-testid="zone-zoom-out">
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="text-xs tabular-nums text-zinc-600 px-2 min-w-[3.5em] text-center" data-testid="zone-zoom-pct">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(MAX_ZOOM, z * 1.25))} className="p-1.5 hover:bg-zinc-100 rounded" title="Zoom in (+)" data-testid="zone-zoom-in">
              <ZoomIn className="h-4 w-4" />
            </button>
            <div className="w-px h-5 bg-zinc-200 mx-1" />
            <button onClick={fitToScreen} className="p-1.5 hover:bg-zinc-100 rounded" title="Fit to screen (0)" data-testid="zone-zoom-fit">
              <Hand className="h-4 w-4" />
            </button>
          </div>
          {drawing && drawMode === "polygon" && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1" data-testid="zone-undo-redo">
              <button onClick={doUndo} disabled={!canUndo()}
                className="p-1.5 hover:bg-zinc-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                title="Undo (Ctrl/⌘+Z)" data-testid="zone-undo">
                <Undo2 className="h-3.5 w-3.5" />
              </button>
              <button onClick={doRedo} disabled={!canRedo()}
                className="p-1.5 hover:bg-zinc-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                title="Redo (Ctrl/⌘+Shift+Z)" data-testid="zone-redo">
                <Redo2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {drawing && (
            <div className="relative inline-flex items-center">
              <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-l-md border border-zinc-200 bg-white text-xs cursor-pointer select-none" title="Snap to existing zone corners + alignment guides">
                <input type="checkbox" checked={snapEnabled} onChange={(e) => setSnapEnabled(e.target.checked)} className="accent-amber-600" data-testid="zone-snap-toggle" />
                Snap
              </label>
              <button
                onClick={() => setSnapMenuOpen((o) => !o)}
                className={`px-1.5 py-[7px] rounded-r-md border border-l-0 border-zinc-200 bg-white hover:bg-zinc-50 ${snapMenuOpen ? "bg-zinc-100" : ""}`}
                title="Snap kinds (endpoint, midpoint, intersection, …)"
                data-testid="zone-snap-kinds-btn"
              >
                <ChevronDown className="h-3.5 w-3.5 text-zinc-600" />
              </button>
              {snapMenuOpen && (
                <div className="absolute z-50 top-full mt-1 left-0 w-48 rounded-md border border-zinc-200 bg-white shadow-lg p-2 space-y-0.5" data-testid="zone-snap-kinds-menu">
                  <div className="px-1 pb-1 text-[10px] uppercase tracking-[0.15em] text-zinc-400">Snap kinds</div>
                  {[
                    ["vertex", "Endpoint / vertex"],
                    ["midpoint", "Midpoint"],
                    ["intersection", "Intersection"],
                    ["edge", "Nearest on edge"],
                    ["perpendicular", "Perpendicular"],
                    ["extension", "Edge extension"],
                  ].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-xs cursor-pointer select-none px-1 py-1 rounded hover:bg-zinc-50">
                      <input
                        type="checkbox"
                        checked={!!snapKinds[k]}
                        onChange={(e) => setSnapKinds((s) => ({ ...s, [k]: e.target.checked }))}
                        className="accent-amber-600"
                        data-testid={`zone-snapkind-${k}`}
                      />
                      {label}
                      <span className="ml-auto text-[9px] font-mono text-zinc-400">{SNAP_KIND_LABEL[k]}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Phase 3.5 — alignment grid + rulers toggles */}
          <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-200 bg-white text-xs cursor-pointer select-none" title="Show alignment grid">
            <input type="checkbox" checked={gridEnabled} onChange={(e) => setGridEnabled(e.target.checked)} className="accent-amber-600" data-testid="zone-grid-toggle" />
            Grid
          </label>
          {gridEnabled && (
            <select
              value={gridSize}
              onChange={(e) => setGridSize(parseInt(e.target.value, 10))}
              className="px-2 py-1 rounded-md border border-zinc-200 bg-white text-xs"
              data-testid="zone-grid-size"
            >
              <option value="8">8 px</option>
              <option value="16">16 px</option>
              <option value="32">32 px</option>
              <option value="64">64 px</option>
            </select>
          )}
          {/* Phase 5C — snap-to-grid toggle (only meaningful with Grid on). */}
          {gridEnabled && drawing && drawMode === "polygon" && (
            <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-200 bg-white text-xs cursor-pointer select-none" title="Snap new / dragged vertices to grid intersections">
              <input type="checkbox" checked={gridSnapEnabled} onChange={(e) => setGridSnapEnabled(e.target.checked)} className="accent-amber-600" data-testid="zone-grid-snap-toggle" />
              Snap to grid
            </label>
          )}
          <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-200 bg-white text-xs cursor-pointer select-none" title="Show pixel rulers">
            <input type="checkbox" checked={rulersEnabled} onChange={(e) => setRulersEnabled(e.target.checked)} className="accent-amber-600" data-testid="zone-rulers-toggle" />
            Rulers
          </label>
          {/* iter56 — Guide lines: infinite construction lines saved per scene */}
          <div className="inline-flex items-center gap-1 rounded-lg border border-sky-300 bg-sky-50 p-1" data-testid="zone-guides-toolbar">
            <button
              onClick={addGuide}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-sky-700 hover:bg-sky-100"
              title="Add an infinite guide line (drag to move, use the blue dot to rotate). Vertices snap to guides and their intersections."
              data-testid="zone-guide-add">
              <Plus className="h-3.5 w-3.5" /> Guide
            </button>
            {selectedGuideId && (
              <button
                onClick={() => deleteGuide(selectedGuideId)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-rose-600 hover:bg-rose-50"
                title="Delete the selected guide line"
                data-testid="zone-guide-delete">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
            {guides.length > 0 && (
              <span className="px-1.5 text-[11px] text-sky-600">{guides.length} guide{guides.length === 1 ? "" : "s"}</span>
            )}
          </div>
          {/* Iter36 — Morpholio Trace ruler (moveable / rotatable / snappy) */}
          {drawing && drawMode === "polygon" && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1" data-testid="zone-ruler-toolbar">
              <label className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer" title="Enable Trace-style ruler — drawn vertices snap to it.">
                <input type="checkbox" checked={ruler.enabled} onChange={(e) => setRuler({ ...ruler, enabled: e.target.checked })} className="accent-amber-600" data-testid="zone-ruler-toggle" />
                Ruler
              </label>
              {ruler.enabled && (
                <>
                  <div className="w-px h-4 bg-zinc-200 mx-0.5" />
                  <button
                    onClick={() => setRuler({ ...ruler, mode: ruler.mode === "straight" ? "perspective" : "straight" })}
                    className="px-2 py-1 rounded text-[10px] uppercase tracking-wider hover:bg-zinc-100"
                    title={ruler.mode === "straight" ? "Switch to perspective ruler" : "Switch to straight ruler"}
                    data-testid="zone-ruler-mode">
                    {ruler.mode === "straight" ? "Straight" : "Perspective"}
                  </button>
                  <button
                    onClick={() => setRuler({ ...ruler, angleDeg: 0 })}
                    className="p-1 rounded hover:bg-zinc-100 text-zinc-500"
                    title="Reset to horizontal"
                    data-testid="zone-ruler-reset-h">
                    <span className="block h-0.5 w-4 bg-current" />
                  </button>
                  <button
                    onClick={() => setRuler({ ...ruler, angleDeg: 90 })}
                    className="p-1 rounded hover:bg-zinc-100 text-zinc-500"
                    title="Rotate to vertical"
                    data-testid="zone-ruler-reset-v">
                    <span className="block h-4 w-0.5 bg-current mx-auto" />
                  </button>
                  <input
                    type="number"
                    value={Math.round(ruler.angleDeg)}
                    onChange={(e) => setRuler({ ...ruler, angleDeg: parseFloat(e.target.value) || 0 })}
                    className="w-14 px-1.5 py-0.5 rounded border border-zinc-200 text-xs tabular-nums"
                    title="Angle in degrees"
                    data-testid="zone-ruler-angle"
                  />
                  <span className="text-xs text-zinc-500">°</span>
                </>
              )}
            </div>
          )}
          {/* Phase 5C — Align + Distribute toolbar (multi-selection). */}
          {drawing && drawMode === "polygon" && selectedVertices.length >= 2 && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 p-1" data-testid="zone-align-toolbar">
              <span className="text-[10px] uppercase tracking-wider text-amber-800 px-1.5">Align</span>
              <button onClick={() => applyAlign("left")}   className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Align left"   data-testid="zone-align-left">   <AlignLeft  className="h-3.5 w-3.5" /></button>
              <button onClick={() => applyAlign("cx")}     className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Align center X" data-testid="zone-align-cx"> <AlignCenterHorizontal className="h-3.5 w-3.5" /></button>
              <button onClick={() => applyAlign("right")}  className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Align right"  data-testid="zone-align-right">  <AlignRight className="h-3.5 w-3.5" /></button>
              <div className="w-px h-4 bg-amber-200 mx-1" />
              <button onClick={() => applyAlign("top")}    className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Align top"    data-testid="zone-align-top">    <AlignStartHorizontal className="h-3.5 w-3.5" /></button>
              <button onClick={() => applyAlign("cy")}     className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Align center Y" data-testid="zone-align-cy"> <AlignVerticalJustifyCenter className="h-3.5 w-3.5" /></button>
              <button onClick={() => applyAlign("bottom")} className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Align bottom" data-testid="zone-align-bottom"> <AlignEndHorizontal className="h-3.5 w-3.5" /></button>
              {selectedVertices.length >= 3 && (
                <>
                  <div className="w-px h-4 bg-amber-200 mx-1" />
                  <span className="text-[10px] uppercase tracking-wider text-amber-800 px-1.5">Distribute</span>
                  <button onClick={() => applyDistribute("x")} className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Distribute horizontally" data-testid="zone-distribute-x"><AlignHorizontalSpaceAround className="h-3.5 w-3.5" /></button>
                  <button onClick={() => applyDistribute("y")} className="p-1.5 hover:bg-amber-100 rounded text-amber-900" title="Distribute vertically"   data-testid="zone-distribute-y"><AlignVerticalSpaceAround   className="h-3.5 w-3.5" /></button>
                </>
              )}
              <span className="text-[10px] tabular-nums text-amber-800 px-1.5">{selectedVertices.length} pts</span>
            </div>
          )}
          {!isFullscreen && (
            <button onClick={() => setExpanded(v => !v)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-200 bg-white text-xs hover:bg-zinc-50 ml-auto" data-testid="zone-expand-toggle">
              {expanded ? "Compact" : "Expand"}
            </button>
          )}
          <button onClick={toggleFullscreen}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs ${isFullscreen ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}
            data-testid="zone-fullscreen-toggle"
            title="Toggle fullscreen">
            {isFullscreen ? <><Minimize2 className="h-3.5 w-3.5" /> Exit fullscreen</> : <><Maximize2 className="h-3.5 w-3.5" /> Fullscreen</>}
          </button>
        </div>

        {/* Canvas */}
        <div
          ref={wrapRef}
          className="relative w-full editor-canvas rounded-xl overflow-hidden border border-zinc-200 bg-zinc-950 select-none"
          style={{ height: canvasHeight, cursor, touchAction: "none" }}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="zone-editor-canvas"
        >
          {/* Media wrapper */}
          {scene?.media_url && (
            <div style={wrapperStyle}>
              {isVideoScene(scene) ? (
                <video
                  ref={mediaRef}
                  src={scene.media_url}
                  width={mediaSize.w}
                  height={mediaSize.h}
                  style={{ display: "block", width: mediaSize.w, height: mediaSize.h, background: "#000" }}
                  autoPlay loop muted playsInline crossOrigin="anonymous"
                  poster={scene.poster_url || undefined}
                  onLoadedMetadata={(e) => {
                    const vw = e.currentTarget.videoWidth;
                    const vh = e.currentTarget.videoHeight;
                    if (vw > 0 && vh > 0) setMediaSize({ w: vw, h: vh });
                  }}
                  onError={() => toast.error("Could not load scene video — check the URL or its CORS config")}
                  data-testid="zone-editor-video"
                />
              ) : (
                <img
                  ref={mediaRef}
                  src={scene.media_url}
                  alt=""
                  width={mediaSize.w}
                  height={mediaSize.h}
                  style={{ display: "block", width: mediaSize.w, height: mediaSize.h }}
                  draggable={false}
                  onLoad={(e) => {
                    const w = e.currentTarget.naturalWidth;
                    const h = e.currentTarget.naturalHeight;
                    if (w > 0 && h > 0) setMediaSize({ w, h });
                  }}
                  data-testid="zone-editor-image"
                />
              )}
              {/* SVG inside the same transform for pixel-perfect alignment */}
              <svg
                width={mediaSize.w}
                height={mediaSize.h}
                viewBox={`0 0 ${mediaSize.w} ${mediaSize.h}`}
                style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
              >
                {/* Phase 3.5 — alignment grid (toggleable) */}
                {gridEnabled && (
                  <g style={{ pointerEvents: "none" }}>
                    <defs>
                      <pattern id="evb-grid" width={gridSize} height={gridSize} patternUnits="userSpaceOnUse">
                        <path d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`}
                          fill="none"
                          stroke="rgba(255,255,255,0.18)"
                          strokeWidth={Math.max(0.5, 0.6 / totalScale)} />
                      </pattern>
                    </defs>
                    <rect width={mediaSize.w} height={mediaSize.h} fill="url(#evb-grid)" />
                  </g>
                )}

                {/* Existing polygon zones */}
                {zones.filter(z => (z.shape || "polygon") === "polygon").map(z => (
                  <polygon
                    key={z.id}
                    points={polyMediaPx(z.polygon_points || [])}
                    style={{
                      fill: editingId === z.id ? "rgba(245,158,11,0.40)" : "rgba(196,164,124,0.20)",
                      stroke: editingId === z.id ? "rgba(245,158,11,0.95)" : "rgba(196,164,124,0.95)",
                      strokeWidth: Math.max(1.2, 1.4 / totalScale),
                      // iter56 — while drawing a NEW zone, existing zones must
                      // NOT capture clicks (that used to hijack into editing and
                      // discard the in-progress polygon). Instead the click
                      // falls through to the canvas and drops a vertex, which
                      // snaps onto this zone's edge (edge-snap in applyVertexSnap).
                      pointerEvents: drawing ? "none" : "auto",
                      cursor: "pointer",
                    }}
                    onClick={(e) => { if (drawing) return; e.stopPropagation(); editZone(z); }}
                    data-testid={`existing-zone-${z.id}`}
                  />
                ))}
                {/* Existing point zones */}
                {zones.filter(z => z.shape === "point" && z.point_coord).map(z => (
                  <g key={z.id} onClick={(e) => { if (drawing) return; e.stopPropagation(); editZone(z); }} style={{ pointerEvents: drawing ? "none" : "auto", cursor: "pointer" }} data-testid={`existing-zone-${z.id}`}>
                    <circle
                      cx={z.point_coord.x * mediaSize.w}
                      cy={z.point_coord.y * mediaSize.h}
                      r={Math.max(8, 14 / totalScale)}
                      fill={editingId === z.id ? "rgba(245,158,11,0.85)" : "rgba(196,164,124,0.85)"}
                      stroke="#000" strokeOpacity={0.55}
                      strokeWidth={Math.max(0.8, 1.2 / totalScale)}
                    />
                    <circle
                      cx={z.point_coord.x * mediaSize.w}
                      cy={z.point_coord.y * mediaSize.h}
                      r={Math.max(14, 26 / totalScale)}
                      fill="rgba(196,164,124,0.18)"
                    />
                  </g>
                ))}
                {/* In-progress polygon */}
                {drawing && drawMode === "polygon" && points.length > 0 && (
                  <>
                    <polyline
                      points={inProgressPx}
                      style={{
                        fill: isSelfIntersecting ? "rgba(239,68,68,0.12)" : "rgba(255,255,255,0.15)",
                        stroke: isSelfIntersecting ? "#f87171" : "#ffffff",
                        strokeDasharray: `${4 / totalScale} ${3 / totalScale}`,
                        strokeWidth: Math.max(1.5, 1.8 / totalScale),
                      }}
                    />
                    {/* Midpoint handles (Phase 3.5) — click to insert vertex on edge */}
                    {points.length >= 2 && points.map((p, i) => {
                      const next = points[(i + 1) % points.length];
                      // Only draw the closing-edge midpoint if there are ≥3 points
                      if (i === points.length - 1 && points.length < 3) return null;
                      const mx = (p.x + next.x) / 2 * mediaSize.w;
                      const my = (p.y + next.y) / 2 * mediaSize.h;
                      return (
                        <circle
                          key={`mid-${i}`}
                          cx={mx} cy={my}
                          r={Math.max(3, 4 / totalScale)}
                          fill="rgba(34,197,94,0.65)"
                          stroke="#fff"
                          strokeWidth={Math.max(0.6, 0.8 / totalScale)}
                          style={{ cursor: "copy", pointerEvents: "auto" }}
                          onPointerDown={(e) => onMidpointClick(e, i)}
                          data-testid={`zone-midpoint-${i}`}
                        />
                      );
                    })}
                    {/* Draggable vertex handles (Phase 3.5 + 5C multi-select) */}
                    {points.map((p, i) => {
                      const inMulti = selectedVertices.includes(i);
                      const isPrimary = selectedVertex === i;
                      return (
                        <circle
                          key={i}
                          cx={p.x * mediaSize.w}
                          cy={p.y * mediaSize.h}
                          r={Math.max(5, (inMulti ? 8 : 7) / totalScale)}
                          fill={inMulti ? "#f59e0b" : isPrimary ? "#fbbf24" : "#fff"}
                          stroke="#000"
                          strokeWidth={Math.max(0.8, (inMulti ? 1.4 : 1) / totalScale)}
                          style={{ cursor: "grab", pointerEvents: "auto" }}
                          onPointerDown={(e) => onVertexPointerDown(e, i)}
                          onContextMenu={(e) => onVertexContextMenu(e, i)}
                          data-testid={`zone-vertex-${i}`}
                        />
                      );
                    })}
                  </>
                )}
                {/* iter56 — Infinite guide lines (per-scene construction grid) */}
                {guides.map((g) => {
                  const cx = g.x * mediaSize.w, cy = g.y * mediaSize.h;
                  const rad = (g.angleDeg || 0) * Math.PI / 180;
                  const L = Math.max(mediaSize.w, mediaSize.h) * 4;
                  const x1 = cx - Math.cos(rad) * L, y1 = cy - Math.sin(rad) * L;
                  const x2 = cx + Math.cos(rad) * L, y2 = cy + Math.sin(rad) * L;
                  const sel = selectedGuideId === g.id;
                  const sw = Math.max(0.7, 1 / totalScale);
                  const rhx = cx + Math.cos(rad) * (70 / totalScale);
                  const rhy = cy + Math.sin(rad) * (70 / totalScale);
                  return (
                    <g key={g.id} data-testid={`zone-guide-${g.id}`}>
                      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(0,0,0,0)"
                        strokeWidth={14 / totalScale}
                        style={{ pointerEvents: drawing ? "none" : "stroke", cursor: "move" }}
                        onPointerDown={(e) => onGuideDown(e, g.id, "move")} />
                      <line x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={sel ? "#0284c7" : "rgba(2,132,199,0.55)"}
                        strokeWidth={sel ? sw * 1.8 : sw}
                        strokeDasharray={`${6 / totalScale} ${5 / totalScale}`}
                        style={{ pointerEvents: "none" }} />
                      {!drawing && (
                        <>
                          <circle cx={cx} cy={cy} r={7 / totalScale} fill="#fff" stroke="#0284c7"
                            strokeWidth={sw * 1.5} style={{ cursor: "move", pointerEvents: "auto" }}
                            onPointerDown={(e) => onGuideDown(e, g.id, "move")}
                            data-testid={`zone-guide-move-${g.id}`} />
                          <circle cx={rhx} cy={rhy} r={6 / totalScale} fill="#0284c7" stroke="#fff"
                            strokeWidth={sw * 1.2} style={{ cursor: "grab", pointerEvents: "auto" }}
                            onPointerDown={(e) => onGuideDown(e, g.id, "rotate")}
                            data-testid={`zone-guide-rotate-${g.id}`} />
                        </>
                      )}
                    </g>
                  );
                })}
                {/* Phase 5C — alignment guide lines */}
                {snapGuides.length > 0 && drawing && drawMode === "polygon" && snapGuides.map((g, i) => g.type === "v" ? (
                  <line key={`g-${i}`}
                    x1={g.value * mediaSize.w} x2={g.value * mediaSize.w}
                    y1={0} y2={mediaSize.h}
                    stroke="#22c55e"
                    strokeWidth={Math.max(0.8, 1 / totalScale)}
                    strokeDasharray={`${3 / totalScale} ${3 / totalScale}`}
                    data-testid={`zone-snap-guide-v-${i}`}
                  />
                ) : (
                  <line key={`g-${i}`}
                    y1={g.value * mediaSize.h} y2={g.value * mediaSize.h}
                    x1={0} x2={mediaSize.w}
                    stroke="#22c55e"
                    strokeWidth={Math.max(0.8, 1 / totalScale)}
                    strokeDasharray={`${3 / totalScale} ${3 / totalScale}`}
                    data-testid={`zone-snap-guide-h-${i}`}
                  />
                ))}
                {/* Iter36 — Morpholio ruler overlay (visible only while drawing) */}
                {ruler.enabled && drawing && drawMode === "polygon" && (() => {
                  const { a, b } = rulerEndpoints(ruler);
                  const cxPx = ruler.cx * mediaSize.w;
                  const cyPx = ruler.cy * mediaSize.h;
                  const rad = (ruler.angleDeg || 0) * Math.PI / 180;
                  const endX = cxPx + Math.cos(rad) * (110 / totalScale);
                  const endY = cyPx + Math.sin(rad) * (110 / totalScale);
                  const strokeW = Math.max(1, 1.4 / totalScale);
                  return (
                    <g data-testid="zone-ruler-overlay" style={{ pointerEvents: "auto" }}>
                      <line x1={a.x * mediaSize.w} y1={a.y * mediaSize.h}
                            x2={b.x * mediaSize.w} y2={b.y * mediaSize.h}
                            stroke="rgba(245,158,11,0.14)"
                            strokeWidth={20 / totalScale} strokeLinecap="round" />
                      <line x1={a.x * mediaSize.w} y1={a.y * mediaSize.h}
                            x2={b.x * mediaSize.w} y2={b.y * mediaSize.h}
                            stroke="#f59e0b" strokeWidth={strokeW * 1.2}
                            strokeDasharray={ruler.mode === "perspective" ? `${6 / totalScale} ${4 / totalScale}` : "0"} />
                      <circle cx={cxPx} cy={cyPx} r={9 / totalScale}
                              fill="#fff" stroke="#f59e0b"
                              strokeWidth={strokeW * 1.4}
                              style={{ cursor: "move" }}
                              onPointerDown={(e) => onRulerHandleDown(e, "translate")}
                              data-testid="zone-ruler-handle-center" />
                      {ruler.mode === "straight" && (
                        <>
                          <line x1={cxPx} y1={cyPx} x2={endX} y2={endY}
                                stroke="#f59e0b" strokeOpacity="0.35" strokeWidth={strokeW * 0.8} />
                          <circle cx={endX} cy={endY} r={7 / totalScale}
                                  fill="#f59e0b" stroke="#fff"
                                  strokeWidth={strokeW * 1.2}
                                  style={{ cursor: "grab" }}
                                  onPointerDown={(e) => onRulerHandleDown(e, "rotate")}
                                  data-testid="zone-ruler-handle-rotate" />
                        </>
                      )}
                      {ruler.mode === "perspective" && (
                        <>
                          <circle cx={ruler.vanishX * mediaSize.w}
                                  cy={ruler.vanishY * mediaSize.h}
                                  r={11 / totalScale}
                                  fill="rgba(245,158,11,0.35)"
                                  stroke="#f59e0b" strokeWidth={strokeW * 1.2}
                                  style={{ cursor: "move" }}
                                  onPointerDown={(e) => onRulerHandleDown(e, "vanish")}
                                  data-testid="zone-ruler-handle-vanish" />
                          <circle cx={ruler.vanishX * mediaSize.w}
                                  cy={ruler.vanishY * mediaSize.h}
                                  r={2 / totalScale}
                                  fill="#f59e0b" style={{ pointerEvents: "none" }} />
                        </>
                      )}
                    </g>
                  );
                })()}
                {/* In-progress point */}
                {drawing && drawMode === "point" && pointCoord && (
                  <>
                    <circle
                      cx={pointCoord.x * mediaSize.w}
                      cy={pointCoord.y * mediaSize.h}
                      r={Math.max(8, 14 / totalScale)}
                      fill="#fff" stroke="#0a0" strokeWidth={Math.max(1.2, 1.6 / totalScale)}
                    />
                    <circle
                      cx={pointCoord.x * mediaSize.w}
                      cy={pointCoord.y * mediaSize.h}
                      r={Math.max(14, 26 / totalScale)}
                      fill="rgba(34,197,94,0.16)"
                    />
                  </>
                )}
                {/* Snap indicator — marker shape + badge per snap kind (CAD OSNAP style) */}
                {snapPreview && drawing && drawMode === "polygon" && (() => {
                  const sx = snapPreview.x * mediaSize.w, sy = snapPreview.y * mediaSize.h;
                  const r = Math.max(10, 14 / totalScale);
                  const sw = Math.max(1.6, 2 / totalScale);
                  const kind = snapPreview.kind || "vertex";
                  const stroke = "#22c55e";
                  return (
                    <g data-testid="zone-snap-indicator" data-snap-kind={kind}>
                      {kind === "vertex" && (
                        <rect x={sx - r * 0.7} y={sy - r * 0.7} width={r * 1.4} height={r * 1.4}
                          fill="none" stroke={stroke} strokeWidth={sw} />
                      )}
                      {kind === "midpoint" && (
                        <polygon points={`${sx},${sy - r * 0.85} ${sx - r * 0.78},${sy + r * 0.6} ${sx + r * 0.78},${sy + r * 0.6}`}
                          fill="none" stroke={stroke} strokeWidth={sw} />
                      )}
                      {kind === "intersection" && (
                        <>
                          <line x1={sx - r * 0.7} y1={sy - r * 0.7} x2={sx + r * 0.7} y2={sy + r * 0.7} stroke={stroke} strokeWidth={sw} />
                          <line x1={sx - r * 0.7} y1={sy + r * 0.7} x2={sx + r * 0.7} y2={sy - r * 0.7} stroke={stroke} strokeWidth={sw} />
                        </>
                      )}
                      {kind === "perpendicular" && (
                        <>
                          <line x1={sx - r * 0.7} y1={sy - r * 0.7} x2={sx - r * 0.7} y2={sy + r * 0.7} stroke={stroke} strokeWidth={sw} />
                          <line x1={sx - r * 0.7} y1={sy + r * 0.7} x2={sx + r * 0.7} y2={sy + r * 0.7} stroke={stroke} strokeWidth={sw} />
                          <rect x={sx - r * 0.7} y={sy + r * 0.15} width={r * 0.55} height={r * 0.55}
                            fill="none" stroke={stroke} strokeWidth={sw * 0.8} />
                        </>
                      )}
                      {(kind === "edge" || kind === "guide") && (
                        <circle cx={sx} cy={sy} r={r} fill="none" stroke={stroke} strokeWidth={sw} />
                      )}
                      {kind === "extension" && (
                        <circle cx={sx} cy={sy} r={r} fill="none" stroke={stroke} strokeWidth={sw}
                          strokeDasharray={`${sw * 2.4} ${sw * 2.4}`} />
                      )}
                      <text x={sx + r + 5} y={sy - r} fill={stroke}
                        fontSize={Math.max(9, 11 / totalScale)}
                        fontFamily="ui-monospace, SFMono-Regular, monospace"
                        style={{ userSelect: "none" }}
                        data-testid="zone-snap-kind-badge">
                        {SNAP_KIND_LABEL[kind] || ""}
                      </text>
                    </g>
                  );
                })()}
              </svg>
            </div>
          )}

          {/* Empty-state message */}
          {!scene?.media_url && (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-sm">
              This scene has no media to draw on.
            </div>
          )}

          {/* Hint banner */}
          {drawing && (
            <div className="absolute top-3 left-3 text-[11px] uppercase tracking-[0.2em] bg-black/65 text-white px-3 py-1.5 rounded pointer-events-none" data-testid="zone-draw-hint">
              {drawMode === "polygon"
                ? <>Click to add points · {points.length} placed{snapPreview ? " · snapping" : ""}{selectedVertices.length > 1 ? ` · ${selectedVertices.length} selected` : (selectedVertex != null ? " · vertex selected" : "")}</>
                : <>{pointCoord ? "Tap a different spot to move the point" : "Tap to place the point"}</>}
            </div>
          )}

          {/* Phase 5C — Self-intersection warning banner */}
          {isSelfIntersecting && (
            <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] bg-red-500/90 text-white px-3 py-1.5 rounded shadow-lg pointer-events-none"
              data-testid="zone-selfintersect-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> Polygon self-intersects
            </div>
          )}

          {/* Phase 3.5 — pixel rulers along top + left edges (toggleable) */}
          {rulersEnabled && scene?.media_url && (
            <Rulers
              containerSize={containerSize}
              mediaSize={mediaSize}
              totalScale={totalScale}
              pan={pan}
            />
          )}
        </div>
      </div>

      {/* Side panel */}
      <div className={`space-y-4 ${isFullscreen ? "fixed right-4 top-20 w-80 max-h-[80vh] overflow-auto bg-white rounded-xl border border-zinc-200 p-4 shadow-xl" : ""}`}>
        {!drawing ? (
          <div className="flex flex-col gap-2">
            <button onClick={() => { setDrawing(true); setDrawMode("polygon"); setPoints([]); }} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700" data-testid="start-draw-zone-btn">
              <Square className="h-4 w-4" /> Draw polygon zone
            </button>
            <button onClick={() => { setDrawing(true); setDrawMode("point"); setPointCoord(null); }} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-zinc-300 bg-white text-sm hover:bg-zinc-50" data-testid="start-draw-point-btn">
              <MapPin className="h-4 w-4" /> Add twinkle point
            </button>
          </div>
        ) : (
          <div className="space-y-3 rounded-xl border border-zinc-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{editingId ? "Edit zone" : (drawMode === "polygon" ? "New polygon zone" : "New twinkle point")}</div>
              {!editingId && (
                <div className="inline-flex items-center gap-1 rounded-md border border-zinc-200 p-0.5">
                  <button onClick={() => { setDrawMode("polygon"); setPointCoord(null); }} className={`text-[11px] px-2 py-1 rounded ${drawMode === "polygon" ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid="zone-mode-polygon">Polygon</button>
                  <button onClick={() => { setDrawMode("point"); setPoints([]); }} className={`text-[11px] px-2 py-1 rounded ${drawMode === "point" ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid="zone-mode-point">Point</button>
                </div>
              )}
            </div>

            {/* Click action picker */}
            <div>
              <div className="text-xs text-zinc-700 mb-1.5">On click</div>
              <select
                value={target.click_action}
                onChange={(e) => setTarget({ ...target, click_action: e.target.value })}
                className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm"
                data-testid="zone-click-action"
              >
                <option value="open_details">{ACTION_LABEL.open_details}</option>
                <option value="show_info">{ACTION_LABEL.show_info}</option>
                <option value="goto_scene">{ACTION_LABEL.goto_scene}</option>
              </select>
            </div>

            {/* open_details linkage */}
            {target.click_action === "open_details" && (
              <>
                <div>
                  <div className="text-xs text-zinc-700 mb-1.5">Link to</div>
                  <select value={target.zone_type} onChange={(e) => setTarget({ ...target, zone_type: e.target.value })} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-target-type">
                    <option value="entity">Unit / entity</option>
                    <option value="group">Block / group</option>
                    <option value="amenity">Amenity (no link)</option>
                  </select>
                </div>
                {target.zone_type === "entity" && (
                  <div>
                    <div className="text-xs text-zinc-700 mb-1.5">Unit</div>
                    <select value={target.entity_id} onChange={(e) => setTarget({ ...target, entity_id: e.target.value })} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-target-entity">
                      <option value="">—</option>
                      {entities.map(e => <option key={e.id} value={e.id}>{e.code} · {e.name}</option>)}
                    </select>
                  </div>
                )}
                {target.zone_type === "group" && (
                  <div>
                    <div className="text-xs text-zinc-700 mb-1.5">Group</div>
                    <select value={target.group_id} onChange={(e) => setTarget({ ...target, group_id: e.target.value })} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-target-group">
                      <option value="">—</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* show_info content */}
            {target.click_action === "show_info" && (
              <>
                <div>
                  <div className="text-xs text-zinc-700 mb-1.5">Info title</div>
                  <input value={target.info_title} onChange={(e) => setTarget({ ...target, info_title: e.target.value })} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-info-title-input" placeholder="e.g. Rooftop infinity pool" />
                </div>
                <div>
                  <div className="text-xs text-zinc-700 mb-1.5">Description</div>
                  <textarea rows={3} value={target.info_description} onChange={(e) => setTarget({ ...target, info_description: e.target.value })} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-info-desc-input" placeholder="Optional — what should the viewer know about this area?" />
                </div>
                <div>
                  <div className="text-xs text-zinc-700 mb-1.5">Image URL <span className="text-zinc-400">(optional)</span></div>
                  <input value={target.info_image_url} onChange={(e) => setTarget({ ...target, info_image_url: e.target.value })} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-info-image-input" placeholder="https://…" />
                </div>
              </>
            )}

            {/* goto_scene */}
            {target.click_action === "goto_scene" && (
              <div>
                <div className="text-xs text-zinc-700 mb-1.5">Jump to scene</div>
                <select value={target.target_scene_id} onChange={(e) => setTarget({ ...target, target_scene_id: e.target.value })} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-target-scene">
                  <option value="">—</option>
                  {otherScenes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div className="text-[11px] text-zinc-500 mt-1">Will use a scrubbable transition automatically if one exists for this jump.</div>
              </div>
            )}

            <div>
              <div className="text-xs text-zinc-700 mb-1.5">Label (optional)</div>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="zone-label" />
            </div>

            <TranslationsEditor
              translations={translations}
              onChange={setTranslations}
              supported={(project?.supported_languages || ["en"]).filter(lang => lang !== (project?.default_language || "en"))}
              keys={target.click_action === "show_info" ? ["name", "info_title", "info_description"] : ["name"]}
              testidPrefix="zone-tr"
            />

            <div className="flex gap-2">
              <button onClick={resetForm} className="flex-1 px-3 py-2 rounded-md border border-zinc-300 text-sm inline-flex items-center justify-center gap-1" data-testid="zone-cancel-btn"><X className="h-4 w-4" /> Cancel</button>
              <button onClick={finishZone} className="flex-1 px-3 py-2 rounded-md bg-zinc-900 text-white text-sm inline-flex items-center justify-center gap-1" data-testid="finish-zone-btn"><Check className="h-4 w-4" /> {editingId ? "Save" : "Finish"}</button>
            </div>

            <div className="text-[11px] text-zinc-500 leading-relaxed">
              {drawMode === "polygon"
                ? <>Click to drop points · need at least 3. Drag vertex = move · Shift-click = multi-select for Align/Distribute · Right-click vertex = delete · Click green midpoint = insert vertex · Ctrl/⌘+Z = undo · Ctrl/⌘+Shift+Z = redo · Scroll = zoom · Space + drag = pan · Esc = cancel.</>
                : <>Tap on the canvas to place the twinkle point. Scroll = zoom · Space + drag = pan · Esc = cancel.</>}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-zinc-200 bg-white">
          <div className="px-4 py-3 border-b border-zinc-200 text-sm font-medium">Existing zones · {zones.length}</div>
          <div className="divide-y divide-zinc-100 max-h-80 overflow-auto">
            {zones.length === 0 && <div className="p-4 text-sm text-zinc-500">No zones yet.</div>}
            {zones.map(z => {
              const e = entities.find(x => x.id === z.entity_id);
              const g = groups.find(x => x.id === z.group_id);
              const ts = z.target_scene_id ? (scenes || []).find(s => s.id === z.target_scene_id) : null;
              const action = z.click_action || "open_details";
              const subtitle = action === "show_info"
                ? `info · ${z.info_title || "(no title)"}`
                : action === "goto_scene"
                  ? `jump → ${ts?.name || "(scene removed)"}`
                  : `${z.zone_type} · ${e ? `unit ${e.code}` : g ? `group ${g.name}` : "—"}`;
              return (
                <div key={z.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-zinc-50">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {z.shape === "point" ? <MapPin className="h-3.5 w-3.5 text-amber-700" /> : <Square className="h-3.5 w-3.5 text-zinc-500" />}
                      {z.name}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{subtitle}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => editZone(z)} className="p-1.5 hover:bg-zinc-100 rounded text-zinc-700" data-testid={`edit-zone-${z.id}`} title="Edit zone settings"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => delZone(z.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`del-zone-${z.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
